import React, { forwardRef, memo } from 'react';
import { CommentForm } from '../CommentForm';
import { classNames } from '../../utils/classNames';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import type { AddCommentProps } from './AddComment.types';
import './AddComment.css';

// Internal component without error boundary
const AddCommentInternal = forwardRef<HTMLDivElement, AddCommentProps>(
  (
    {
      className,
      containerStyle,
      children,
      theme = 'light',
      ...commentFormProps
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={classNames('add-comment', className)}
        style={containerStyle}
        data-theme={theme}
      >
        <CommentForm {...commentFormProps}>{children}</CommentForm>
      </div>
    );
  }
);

AddCommentInternal.displayName = 'AddCommentInternal';

/**
 * AddComment - A container component with built-in error boundary protection
 * Automatically wraps the component with ErrorBoundary for crash protection
 */
const AddCommentComponent = forwardRef<HTMLDivElement, AddCommentProps>(
  ({ errorBoundaryProps, ...props }, ref) => {
    // If error boundary is disabled, return the internal component directly
    if (errorBoundaryProps?.disabled) {
      return <AddCommentInternal ref={ref} {...props} />;
    }

    // Wrap with internal error boundary for automatic protection
    return (
      <ErrorBoundary
        componentName="AddComment"
        fallback={errorBoundaryProps?.fallback}
        onError={errorBoundaryProps?.onError}
        showErrorDetails={errorBoundaryProps?.showErrorDetails}
      >
        <AddCommentInternal ref={ref} {...props} />
      </ErrorBoundary>
    );
  }
);

AddCommentComponent.displayName = 'AddComment';
export const AddComment = memo(AddCommentComponent);
export default AddComment;
